/**
 * API Client
 * Axios instance with authentication interceptors
 */

import axios, { AxiosError, AxiosRequestConfig, AxiosResponse } from 'axios';
import type { ApiResponse, ApiError } from 'blia-events-shared';
import { isConnectionFailure } from './stadiumCheckin/reachability';
import { correlationId } from './correlationId';

const API_BASE_URL = import.meta.env.VITE_API_BASE_URL || '/api';

/**
 * Custom API Error class that preserves the error code for translation
 */
export class ApiClientError extends Error {
  public readonly code: string;
  public readonly details?: Record<string, unknown>;
  /**
   * HTTP status the server replied with, or `undefined` when the request never
   * got a response at all (offline, DNS/TLS failure, axios timeout).
   *
   * Callers that must not lose work — the Scan Station's offline queue — use
   * this to tell "the server deliberately refused" (4xx, replay won't help)
   * apart from "the server failed to answer" (5xx / no response, replay will).
   */
  public readonly status?: number;
  /**
   * The connection never opened, so the server cannot have received this
   * request (see services/stadiumCheckin/reachability.ts). The Scan Station's
   * offline queue uses it to decide whether a queued scan may be undone
   * locally or only through the server.
   */
  public readonly connectionFailed?: boolean;

  constructor(
    code: string,
    message: string,
    details?: Record<string, unknown>,
    status?: number,
    connectionFailed?: boolean,
  ) {
    super(message);
    this.name = 'ApiClientError';
    this.code = code;
    this.details = details;
    this.status = status;
    this.connectionFailed = connectionFailed;
  }
}

/**
 * Type guard to check if an error is an ApiClientError
 */
export function isApiClientError(error: unknown): error is ApiClientError {
  return error instanceof ApiClientError;
}

export const api = axios.create({
  baseURL: API_BASE_URL,
  timeout: 30000,
  headers: {
    'Content-Type': 'application/json',
  },
  withCredentials: true,
});

// Request interceptor
api.interceptors.request.use(
  config => {
    // Correlation ID for request tracing; the API's cross-site write guard also
    // requires it from browsers that send no Sec-Fetch-Site (see ./correlationId).
    config.headers['x-correlation-id'] = correlationId();

    // Add CSRF token if available
    const csrfToken = document.querySelector<HTMLMetaElement>('meta[name="csrf-token"]')?.content;
    if (csrfToken) {
      config.headers['x-csrf-token'] = csrfToken;
    }

    return config;
  },
  error => Promise.reject(error)
);

// Response interceptor
api.interceptors.response.use(
  (response: AxiosResponse) => response,
  (error: AxiosError<{ error: ApiError | string; data?: Record<string, unknown>; message?: string }>) => {
    if (error.response) {
      const { status, data } = error.response;

      // Handle authentication errors - don't auto-redirect to prevent infinite loops
      if (status === 401) {
        return Promise.reject(new ApiClientError('AUTH_REQUIRED', 'Authentication required', undefined, status));
      }

      // Handle forbidden errors
      if (status === 403) {
        const errObj = typeof data?.error === 'object' ? data.error : null;
        const code = errObj?.code || (typeof data?.error === 'string' ? data.error : 'FORBIDDEN');
        const message = errObj?.message || data?.message || 'Access denied';
        return Promise.reject(new ApiClientError(code, message, errObj?.details, status));
      }

      // Handle rate limiting
      if (status === 429) {
        const retryAfter = error.response.headers['retry-after'] || '60';
        return Promise.reject(new ApiClientError('RATE_LIMIT_EXCEEDED', `Rate limit exceeded. Please try again in ${retryAfter} seconds.`, undefined, status));
      }

      // Two response shapes are accepted from the API:
      //   1. Standard envelope: { success: false, error: { code, message, details } }
      //   2. Transportation conflict shape: { error: 'kind_string', message?: string, data: {...} }
      // The interceptor normalizes both into ApiClientError so callers don't have
      // to branch on the shape. The original `data` payload is preserved as details
      // so consumers can read structured fields (e.g., past_departure scheduledTime).
      let errorCode: string;
      let errorMessage: string;
      let details: Record<string, unknown> | undefined;

      if (data && typeof data.error === 'object' && data.error !== null) {
        errorCode = data.error.code || 'UNKNOWN_ERROR';
        errorMessage = data.error.message || 'An unexpected error occurred';
        details = data.error.details;
      } else if (data && typeof data.error === 'string') {
        errorCode = data.error;
        errorMessage = data.message || data.error;
        details = data.data ?? undefined;
      } else {
        errorCode = 'UNKNOWN_ERROR';
        errorMessage = 'An unexpected error occurred';
      }

      return Promise.reject(new ApiClientError(errorCode, errorMessage, details, status));
    }

    // Request was sent but no response came back: offline, DNS/TLS failure, or
    // the axios timeout fired. `status` stays undefined — that is the signal
    // callers use to treat it as retriable rather than a refusal.
    if (error.request) {
      return Promise.reject(
        new ApiClientError(
          'NETWORK_ERROR',
          'Network error. Please check your connection.',
          undefined,
          undefined,
          isConnectionFailure(error),
        ),
      );
    }

    return Promise.reject(error);
  }
);

// Typed API helpers
export async function get<T>(url: string, config?: AxiosRequestConfig): Promise<T> {
  const response = await api.get<T>(url, config);
  return response.data;
}

export async function post<T, D = unknown>(url: string, data?: D, config?: AxiosRequestConfig): Promise<T> {
  const response = await api.post<T>(url, data, config);
  return response.data;
}

export async function patch<T, D = unknown>(url: string, data?: D, config?: AxiosRequestConfig): Promise<T> {
  const response = await api.patch<T>(url, data, config);
  return response.data;
}

export async function put<T, D = unknown>(url: string, data?: D, config?: AxiosRequestConfig): Promise<T> {
  const response = await api.put<T>(url, data, config);
  return response.data;
}

export async function del<T>(url: string, config?: AxiosRequestConfig): Promise<T> {
  const response = await api.delete<T>(url, config);
  return response.data;
}

// File upload helper
export async function uploadFile<T>(
  url: string,
  file: File,
  onProgress?: (progress: number) => void
): Promise<T> {
  const formData = new FormData();
  formData.append('file', file);

  const response = await api.post<{ success: boolean; data: T }>(url, formData, {
    headers: {
      'Content-Type': 'multipart/form-data',
    },
    onUploadProgress: progressEvent => {
      if (progressEvent.total && onProgress) {
        const progress = Math.round((progressEvent.loaded * 100) / progressEvent.total);
        onProgress(progress);
      }
    },
  });

  // Unwrap the API response envelope
  return response.data.data;
}

// File download helper
export async function downloadFile(url: string, filename: string): Promise<void> {
  const response = await api.get(url, {
    responseType: 'blob',
  });

  const blob = new Blob([response.data]);
  const downloadUrl = window.URL.createObjectURL(blob);
  const link = document.createElement('a');
  link.href = downloadUrl;
  link.download = filename;
  document.body.appendChild(link);
  link.click();
  document.body.removeChild(link);
  window.URL.revokeObjectURL(downloadUrl);
}

// API client object for convenience
export const apiClient = {
  get,
  post,
  patch,
  put,
  delete: del,
};

export default api;
